import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader.tsx";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import type { ClientInfo } from "../../../../../../api/types/clientTypes.ts";

interface Props {
  isCreatingReportTemplateDraft?: boolean;
  companies: ClientInfo[];
  onClose: () => void;
  onCreate: (clientCode: string) => void;
}

export default function NewTemplateDialog({ companies, isCreatingReportTemplateDraft, onClose, onCreate }: Props) {
  const [selectedCompany, setSelectedCompany] = useState<string>(() => companies[0]?.clientCode || "");

  const handleChange = useCallback((clientCode: string) => {
    setSelectedCompany(clientCode);
  }, []);

  return (
    <PromptDialog open={true} sx={{ width: 517, minHeight: 212 }}>
      <DialogHeader text="New Template" onClose={onClose} />
      <Stack
        direction="column"
        useFlexGap
        spacing={2}
        sx={{
          flex: 1,
          alignItems: "flex-start",
          justifyContent: "center",
          pt: 1,
          pb: 2.5,
          px: 3,
        }}
      >
        <Typography sx={{ color: "text.primary" }}>Select the company whose data you want to use.</Typography>
        <FormControl fullWidth>
          <InputLabel id="company-select-label">Company</InputLabel>
          <Select
            labelId="company-select-label"
            label="Company"
            value={selectedCompany}
            renderValue={(value) => {
              const logo = companies.find((c) => c.clientCode === value)?.branding.logoMarkUrl;
              return (
                <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", pl: 0.25, gap: 1.5 }}>
                  {logo && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        bgcolor: "background.paper",
                      }}
                    >
                      <img alt="Client Logo" style={{ width: "20px", height: "20px" }} src={logo} />
                    </Box>
                  )}
                  <Typography>{companies.find((c) => c.clientCode === value)?.title || ""}</Typography>
                </Box>
              );
            }}
            onChange={(e) => handleChange(e.target.value)}
          >
            {companies.map((company) => (
              <MenuItem key={company.clientCode} value={company.clientCode} sx={{ py: 0.5, gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: 20,
                    height: 20,
                    p: 0.5,
                    bgcolor: "background.paper",
                  }}
                >
                  <img alt="Client Logo" style={{ width: "20px", height: "20px" }} src={company.branding.logoMarkUrl} />
                </Box>
                <Typography>{company.title}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          loading={isCreatingReportTemplateDraft}
          disabled={!selectedCompany || isCreatingReportTemplateDraft}
          onClick={() => selectedCompany && onCreate(selectedCompany)}
        >
          Create
        </Button>
      </DialogButtons>
    </PromptDialog>
  );
}
